import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LazyLoadModule } from '../directives/lazy-load/lazy-load.module';
import { isEqual } from '@fgb/core';
import { exhaustMap, fromEvent, map, merge, takeUntil, tap } from 'rxjs';
import { LazyLoadItemDirective } from '../directives/lazy-load/lazy-load-item.directive';
import gsap from 'gsap';
import Flip from 'gsap/Flip';

gsap.registerPlugin(Flip);

@Component({
  selector: 'fgb-custom-carousel',
  standalone: true,
  imports: [CommonModule, LazyLoadModule],
  templateUrl: './custom-carousel.component.html',
  styleUrl: './custom-carousel.component.scss',
})
export class CustomCarouselComponent implements AfterViewInit, OnDestroy {
  @ViewChild('carousel') carouselRef!: ElementRef<HTMLElement>;
  @ContentChild(TemplateRef, { static: true }) template!: TemplateRef<any>;
  @ViewChildren(LazyLoadItemDirective) lazyLoadItems: QueryList<LazyLoadItemDirective>;
  @Input() height: number = 150;
  @Input() margin: number = 15;
  @Input() visibleItems: number = 3;
  @Input() mobileVisibleItems: number = 1.2;
  @Input() showNavigation: boolean = true;
  @Input() dragSensitivity: number = 1;
  @Output() lastItemIntersected = new EventEmitter<void>();
  private _el: ElementRef = inject(ElementRef);
  private itemsToShow = this.visibleItems;

  private _items: any[] = [];
  @Input()
  set items(val: any[]) {
    if (!isEqual(this._items, val)) {
      this._items = val || [];
      this._init();
    }
  }
  get items(): any[] {
    return this._items;
  }

  get el() {
    return this._el;
  }

  currentIndex: number = 0;
  itemWidth: number = 0;
  maxIndex: number = 0;
  translateX: number = 0;
  isDragging = false;

  private visiblePercent: number = 1;
  private gapPercent: number = 0;
  private startX: number = 0;
  private dragThreshold = 5; // minimal drag distance to consider a swipe
  private resizeObserver!: ResizeObserver;
  isColumn: boolean = false;

  ngAfterViewInit(): void {
    this._init();
    this._setupListeners();
    this._setupResizeObserver();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.carouselRef.nativeElement);
      this.resizeObserver.disconnect();
    }
  }

  next(): void {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateStyle();
    }
  }

  prev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateStyle();
    }
  }

  private _init(): void {
    if (!this.carouselRef?.nativeElement) return;

    const container = this.carouselRef.nativeElement;
    this.gapPercent = (this.margin / container.clientWidth) * 100;
    this.itemWidth = 100 / this.itemsToShow - this.gapPercent;
    this.visiblePercent = 1 - (this.itemsToShow - Math.floor(this.itemsToShow)) / 2;
    this.maxIndex = Math.max(0, this.items.length - this.itemsToShow);
    this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
    this.updateStyle();
  }

  private updateStyle(): void {
    const baseTranslateX =
      (this.itemWidth + this.gapPercent) * this.visiblePercent + (this.currentIndex - 1) * (this.itemWidth + this.gapPercent);

    const maxTranslateX =
      (this.items.length - this.itemsToShow) * (this.itemWidth + this.gapPercent) +
      (this.itemWidth + this.gapPercent) * this.visiblePercent;
    this.translateX = this._clamp(baseTranslateX, 0, maxTranslateX);
  }

  private _setupListeners(): void {
    const carousel = this.carouselRef.nativeElement;

    const pointerStart$ = merge(
      fromEvent<TouchEvent>(carousel, 'touchstart', { passive: false }),
      fromEvent<MouseEvent>(carousel, 'mousedown')
    );

    const pointerMove$ = merge(
      fromEvent<TouchEvent>(carousel, 'touchmove', { passive: false }),
      fromEvent<MouseEvent>(carousel, 'mousemove')
    );

    const pointerEnd$ = merge(fromEvent<MouseEvent>(document, 'mouseup'), fromEvent<TouchEvent>(document, 'touchend')).pipe(
      tap(() => this._onDragEnd())
    );

    pointerStart$
      .pipe(
        exhaustMap((start: TouchEvent | MouseEvent) => {
          this.startX = this._getClientX(start);

          // Prevent default scroll if touch
          if (start instanceof TouchEvent) {
            if (this.isDragging) start.preventDefault();
          }

          return pointerMove$.pipe(
            map((move) => {
              this.isDragging = true;
              const dx = this._getClientX(move) - this.startX;
              if (Math.abs(dx) > this.dragThreshold) {
                move.preventDefault?.(); // prevent scroll while swiping
              }
              return dx;
            }),
            takeUntil(pointerEnd$)
          );
        })
      )
      .subscribe((dx) => this._onDragMove(dx));
  }

  private _onDragMove(dx: number): void {
    const containerWidth = this.carouselRef.nativeElement.clientWidth;
    const deltaPercent = ((dx * this.dragSensitivity) / containerWidth) * 100;

    const itemSize = this.itemWidth + this.gapPercent;
    const baseTranslateX = itemSize * this.currentIndex;
    const maxTranslateX = this.items.length * itemSize - this.itemsToShow * this.visiblePercent * itemSize;
    this.translateX = this._clamp(baseTranslateX - deltaPercent, 0, maxTranslateX);
  }

  private _onDragEnd(): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    const itemSize = this.itemWidth + this.gapPercent;
    const rawIndex = this.translateX / itemSize;
    this.currentIndex = Math.round(rawIndex);
    this.currentIndex = this._clamp(this.currentIndex, 0, Math.round(this.maxIndex));
    this.updateStyle(); // Apply snapped position
  }

  private _getClientX(event: TouchEvent | MouseEvent): number {
    return event instanceof TouchEvent ? event.touches[0].clientX : event.clientX;
  }

  private _setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.itemsToShow = window.innerWidth < 991 ? this.mobileVisibleItems : this.visibleItems;
      this._init();
    });
    this.resizeObserver.observe(this.carouselRef.nativeElement);
  }

  private _clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  // Test
  toggle(): void {
    this.isColumn = !this.isColumn;
    const state = Flip.getState('.custom-carousel-item, .carousel-track');
    // const state1 = Flip.getState('.custom-carousel');

    setTimeout(() => {
      Flip.from(state, {
        duration: 0.6,
        ease: 'power2.inOut',
        absolute: true,
        onEnter: (elements) => gsap.fromTo(elements, { opacity: 0 }, { opacity: 1, duration: 0.3 }),
        onLeave: (elements) => gsap.to(elements, { opacity: 0, duration: 0.3 }),
      });
      // Flip.to(state1, {
      //   duration: 0.6,
      //   ease: 'power2.inOut',
      //   height: 'auto',
      // });
    }, 0);
  }
}
